import { ApiClientBase } from '../base/api-client-base.ts';
import Endpoints from '../endpoints';
import type {
  IAdminApi,
  AdminEnvelope,
  AdminOverview,
  AdminUsersPage,
  AdminUsersQuery,
  UserAnalytics,
  ChangeUserPlanParams,
  ImpersonateResult,
  B2BAccountListItem,
  CreateB2BAccountParams,
  CreateB2BAccountResult,
  B2BAccountDetail,
  AssignSeatResult,
  ApiKeyCreationResult,
  AdminAuditEntry,
  OrgVaultRow,
} from '@/services/api';
import type { SubscriptionPlan } from '@/services/api';

export class AdminApi extends ApiClientBase implements IAdminApi {
  public readonly getOverview = async (): Promise<AdminOverview> => {
    const response = await this.asyncRunner(() =>
      this.api.get<AdminEnvelope<AdminOverview>>(Endpoints.Admin.getOverview())
    );
    return response.data.data;
  };

  public readonly listUsers = async (query: AdminUsersQuery): Promise<AdminUsersPage> => {
    const response = await this.asyncRunner(() =>
      this.api.get<AdminEnvelope<AdminUsersPage>>(Endpoints.Admin.listUsers(), { params: query })
    );
    return response.data.data;
  };

  public readonly getUserAnalytics = async (userId: string): Promise<UserAnalytics> => {
    const response = await this.asyncRunner(() =>
      this.api.get<AdminEnvelope<UserAnalytics>>(Endpoints.Admin.getUserAnalytics(userId))
    );
    return response.data.data;
  };

  public readonly changeUserPlan = async (
    userId: string,
    params: ChangeUserPlanParams
  ): Promise<{ user_id: string; plan: SubscriptionPlan }> => {
    const response = await this.asyncRunner(() =>
      this.api.post<AdminEnvelope<{ user_id: string; plan: SubscriptionPlan }>>(Endpoints.Admin.changeUserPlan(userId), params)
    );
    return response.data.data;
  };

  public readonly impersonate = async (userId: string): Promise<ImpersonateResult> => {
    const response = await this.asyncRunner(() =>
      this.api.post<AdminEnvelope<ImpersonateResult>>(Endpoints.Admin.impersonate(userId))
    );
    return response.data.data;
  };

  public readonly listB2BAccounts = async (): Promise<{ accounts: B2BAccountListItem[] }> => {
    const response = await this.asyncRunner(() =>
      this.api.get<AdminEnvelope<{ accounts: B2BAccountListItem[] }>>(Endpoints.Admin.listB2BAccounts())
    );
    return response.data.data;
  };

  public readonly createB2BAccount = async (params: CreateB2BAccountParams): Promise<CreateB2BAccountResult> => {
    const response = await this.asyncRunner(() =>
      this.api.post<AdminEnvelope<CreateB2BAccountResult>>(Endpoints.Admin.createB2BAccount(), params)
    );
    return response.data.data;
  };

  public readonly getB2BAccount = async (orgId: string): Promise<B2BAccountDetail> => {
    const response = await this.asyncRunner(() =>
      this.api.get<AdminEnvelope<B2BAccountDetail>>(Endpoints.Admin.getB2BAccount(orgId))
    );
    return response.data.data;
  };

  public readonly assignSeat = async (orgId: string, email: string): Promise<AssignSeatResult> => {
    const response = await this.asyncRunner(() =>
      this.api.post<AdminEnvelope<AssignSeatResult>>(Endpoints.Admin.assignSeat(orgId), { email })
    );
    return response.data.data;
  };

  public readonly createApiKey = async (orgId: string, name: string): Promise<ApiKeyCreationResult> => {
    const response = await this.asyncRunner(() =>
      this.api.post<AdminEnvelope<ApiKeyCreationResult>>(Endpoints.Admin.createApiKey(orgId), { name })
    );
    return response.data.data;
  };

  public readonly revokeApiKey = async (orgId: string, keyId: string): Promise<{ revoked: boolean }> => {
    const response = await this.asyncRunner(() =>
      this.api.delete<AdminEnvelope<{ revoked: boolean }>>(Endpoints.Admin.revokeApiKey(orgId, keyId))
    );
    return response.data.data;
  };

  public readonly listAuditLog = async (): Promise<{ entries: AdminAuditEntry[] }> => {
    const response = await this.asyncRunner(() =>
      this.api.get<AdminEnvelope<{ entries: AdminAuditEntry[] }>>(Endpoints.Admin.listAuditLog())
    );
    return response.data.data;
  };

  // backend-v2 endpoint (absolute URL): returns a raw entity array, no AdminEnvelope.
  public readonly listOrgVaults = async (orgId: string): Promise<OrgVaultRow[]> => {
    const response = await this.asyncRunner(() =>
      this.api.get<OrgVaultRow[]>(Endpoints.Admin.listOrgVaults(orgId))
    );
    return response.data;
  };
}
